import { isRechargeParamsRefreshTrade } from "@/store/rechargeParamsRefresh";
import { RechargeCooldownError } from "@/store/rechargeCooldown";
import { useState, useEffect, useRef } from "react";
import { useGetLoginSpecialActivityData } from "@/store/loginSpecialActivity";
import {
  useChangeFlatRecharge,
  useWalletAll,
  useWalletRechargeFlat,
} from "@/store/walletStore";
import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus";
import { sys } from "@/Core/utils/system";
import useDialogManager from "@/utils/useDialogManager";
import { feedback } from "@/utils/feedback";
import {
  AmountDivisorConversion,
  AmountMultiplierConversion,
} from "@/Core/utils/currencyDealCenter";
import { useWsUpdateBanblce } from "@/store/hallSocket";

/**
 * 首充功能共享Hook，提供首充相关数据和操作
 * @returns 首充相关数据和操作方法
 */
export const useFirstUpRecharge = () => {
  const { t } = useTranslationPlus("FirstUpElement");
  const { data: activityData, revalidate } = useGetLoginSpecialActivityData();
  const leftTime = activityData?.timeRechargeRebate?.leftTime * 1000;
  const { data: walletData, isLoading: isWalletLoading } = useWalletAll(true);
  const { trigger, isMutating, isRechargeBlocked } = useWalletRechargeFlat();
  const [isLoading, setIsLoading] = useState(false);
  const [timeOutData, setTimeOutData] = useState<number>(0);
  const { openDialogIFrame } = useDialogManager();
  const { t: tWallet } = useTranslationPlus("Wallet");
  const { data: wsData } = useWsUpdateBanblce();
  // 使用 ref 保存 revalidate 函数，避免因其引用变化导致 effect 循环执行
  const revalidateRef = useRef(revalidate);
  revalidateRef.current = revalidate;

  // 默认使用钱包中第一个法币类型
  const displayCurrencyType = walletData?.flat?.[0]?.currencyType;
  const serverCurrencyType =
    walletData?.flat?.[0]?.serverCurrencyType || displayCurrencyType;

  // 获取充值数据
  const rechargeFlatParams = serverCurrencyType
    ? {
        currencyType: serverCurrencyType,
        device: sys.device,
      }
    : null;
  const { data: flatChargeData, isLoading: isFlatDataLoading } =
    useChangeFlatRecharge(rechargeFlatParams);

  useEffect(() => {
    if (leftTime) {
      setTimeOutData(leftTime);
    }
  }, [leftTime]);

  // 处理WebSocket充值更新（只依赖 wsData，使用 ref 调用 revalidate 避免循环请求）
  // 只在存在首充活动时才刷新，避免不必要的接口请求
  useEffect(() => {
    if (!isRechargeParamsRefreshTrade(wsData?.tradeModeCode)) {
      return;
    }
    // 只有当存在首充活动数据时才需要刷新
    if (!activityData?.timeRechargeRebate) {
      return;
    }
    const timer = setTimeout(() => {
      revalidateRef.current?.();
    }, 500);
    return () => clearTimeout(timer);
  }, [wsData]);

  /**
   * 处理首充按钮点击
   */
  const handleFirstUpRecharge = () => {
    // 检查所有必要数据是否已加载
    if (isRechargeBlocked || isLoading || isMutating || isWalletLoading || isFlatDataLoading) {
      return;
    }

    // 检查是否有必要的数据
    if (
      !activityData?.timeRechargeRebate?.rechargeAmount ||
      !flatChargeData?.payParams?.length
    ) {
      feedback.error(t("Request_Failure"));
      return;
    }

    setIsLoading(true);

    // 获取首充数据中的金额
    const amount = activityData.timeRechargeRebate.rechargeAmount;
    // 使用第一个支付方式的 payCode (默认MTN: 632, 或Vodafone: 633, AirtelTigo: 634)
    const payCode = flatChargeData.payParams[0].payCode;
    // 除以 rebateCurrencyType 的汇率，获取最终金额
    const finalAmount = AmountDivisorConversion(amount, {
      currencyType: activityData.timeRechargeRebate.rebateCurrencyType,
    });

    // 触发充值
    trigger({
      currencyType: serverCurrencyType,
      money: AmountMultiplierConversion(finalAmount, {
        currencyType: displayCurrencyType,
        serverCurrencyType: serverCurrencyType,
      }),
      payCode: payCode,
    })
      .then((res) => {
        if (res?.payUrl) {
          openDialogIFrame({
            title: tWallet("Wallet_PayUrlTitle"),
            url: res?.payUrl,
            type: "pay",
          });
        } else {
          feedback.success(tWallet("Payment request submitted successfully"));
        }
      })
      .catch((e) => {
        if (e instanceof RechargeCooldownError) return;
        feedback.error(t(e?.code || "Request_Failure"));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return {
    activityData,
    revalidate,
    timeOutData,
    isFirstUpAvailable: !!activityData?.timeRechargeRebate,
    isLoading: isLoading || isMutating || isWalletLoading || isFlatDataLoading,
    isRechargeBlocked,
    handleFirstUpRecharge,
  };
};
